import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, RefreshCw, Trash2, X } from 'lucide-react';
import { linearService, type LegActionName, type LegActionResult, type LegActionScope, type LegStateServer } from '@/services/linear.service';
import type { ConfirmActionConfig } from '@/components/ui/ConfirmActionModal';
import { serverFailureReasonKey } from '@/utils/server-reads';
import { getErrorMessage } from '@/services/api';
import { LEG_VERDICTS, heldBadge, legReloadMode, realStatusKey, seenAgo, serverStateKey, serverTypeBadge, verdictClass, verdictHintKey, verdictLabelKey } from '@/utils/leg-state';
import {
  BAR_ACTIONS, DANGER_ACTIONS, actionableCount, disabledRowActions, formatLastActionAt, runScopes, notDesiredLegs, planToConfirmRows, resultSummary, retryScope, rowScope, scopesFromPlan, selectableServerIds, skipReasonKey, toggleAllServers,
} from '@/utils/leg-actions';

function formatUptime(seconds?: number): string {
  if (!seconds || seconds <= 0) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

interface Props {
  channelId: number;
  /** The page bumps it after an action of its own (update config, sync): the panel reads the servers again, fresh. */
  actionTick: number;
  /** Opens the page's confirmation modal (preview + Confirm). */
  openConfirm: (config: ConfirmActionConfig) => void;
  /** After a scoped action ran (the page reloads its channel list). */
  onActed?: () => void;
  /** Every load: the page uses it to know whether the channel has legs. */
  onLoaded?: (servers: LegStateServer[]) => void;
}

/**
 * Xaccel tab, phase 3: the real state per server (GET /linear/:id/legs) with the scoped actions. No server is ticked
 * at first; every action (row button, action bar, "no longer selected" delete) asks the server for a dry-run plan,
 * shows it in the confirmation modal and runs only on Confirm, one request per server, on exactly what the preview
 * showed. An unreachable server cannot be ticked (the reason is shown); "Seleziona tutti i server" only selects.
 */
export default function LegsPanel({ channelId, actionTick, openConfirm, onActed, onLoaded }: Props) {
  const { t } = useTranslation();
  const [servers, setServers] = useState<LegStateServer[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const [barAction, setBarAction] = useState<LegActionName>('create');
  const [planning, setPlanning] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<{ action: LegActionName; results: LegActionResult[] } | null>(null);
  const lastTick = useRef<number | null>(null);
  const lastChannel = useRef<number | null>(null);
  const seq = useRef(0);
  const busyRef = useRef(false);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const load = async (refresh: boolean) => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const data = await linearService.listLegs(channelId, refresh);
      if (mine !== seq.current) return;
      setServers(data);
      setFailed(false);
      // A server that became unreachable is unticked (its actions are disabled).
      setTicked((cur) => new Set([...cur].filter((id) => data.some((s) => s.id === id && s.reachable))));
      onLoadedRef.current?.(data);
    } catch {
      if (mine === seq.current) setFailed(true);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (lastChannel.current !== channelId) {
      lastChannel.current = channelId; lastTick.current = null; setTicked(new Set()); setResults(null);
    }
    const mode = legReloadMode(lastTick.current, actionTick);
    lastTick.current = actionTick;
    if (mode !== 'none') void load(mode === 'refresh');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, actionTick]);

  const ago = (ms: number | null) => {
    const a = seenAgo(ms);
    return t(a.key, a.count !== undefined ? { count: a.count } : undefined);
  };

  /** Plan (dry run) -> preview modal -> on Confirm, the action on exactly the previewed rows, one call per server. */
  const ask = async (action: LegActionName, scope: LegActionScope) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setPlanning(true);
    let items;
    try {
      items = await linearService.planLegs(channelId, action, scope);
    } catch (error) {
      toast.error(t('linear.legs.planFailed', { error: getErrorMessage(error) }));
      return;
    } finally {
      busyRef.current = false;
      setPlanning(false);
    }
    const preview = planToConfirmRows(items);
    const scopes = scopesFromPlan(items);
    if (preview.acting === 0 || scopes.length === 0) {
      toast(t('linear.legs.nothingToDo'));
      return;
    }
    openConfirm({
      title: t(`linear.legs.bar.${action}`, { count: preview.acting, servers: preview.rows.map((r) => r.name).join(', ') }),
      intro: `${t('linear.legs.previewIntro')} ${t(`linear.legs.actionHint.${action}`)}`,
      servers: preview.rows.map((r) => ({
        ...r,
        entries: r.entries.map((e) => ({
          text: e.name,
          detail: `${t(`linear.legs.op.${e.op}`, { defaultValue: e.op })} ·`,
          danger: e.danger,
          note: [
            `${t(verdictLabelKey(e.verdict))}, ${t(realStatusKey(e.realStatus === 'missing' || e.realStatus === 'unknown' ? e.realStatus : { status: e.realStatus as 'running', ref: '' }))}`,
            e.untagged ? t('linear.legs.untaggedNote') : '',
          ].filter(Boolean).join(' · '),
        })),
      })),
      skipped: preview.skipped,
      confirmLabel: t(`linear.legs.action.${action}`),
      tone: DANGER_ACTIONS.includes(action) ? 'danger' : 'primary',
      run: async () => {
        setRunning(true);
        try {
          const all: LegActionResult[] = [];
          try {
            for (const s of runScopes(action, scopes)) all.push(...await linearService.runLegAction(channelId, action, s));
          } catch (error) {
            // A long action (restart with keys on several legs) can outlast the browser timeout while the server goes on:
            // never an "error" then, the state below is read again and the results come from the servers.
            if ((error as { code?: string })?.code === 'ECONNABORTED' || /timeout/i.test((error as Error)?.message || '')) {
              toast(t('linear.legs.stillRunning'), { duration: 10000 });
              return;
            }
            throw error;
          }
          setResults({ action, results: all });
          const sum = resultSummary(all);
          if (sum.failed > 0) toast.error(t('linear.legs.results.summary', { done: sum.done, already: sum.alreadyDone, failed: sum.failed }));
          else toast.success(t('linear.legs.results.summary', { done: sum.done, already: sum.alreadyDone, failed: 0 }));
        } finally {
          setRunning(false);
          onActed?.();
          await load(true);
        }
      },
    });
  };

  const bar = useMemo(() => (servers ? actionableCount(servers, ticked, barAction) : { count: 0, serverNames: [] }), [servers, ticked, barAction]);
  const selectable = servers ? selectableServerIds(servers) : [];
  const allTicked = selectable.length > 0 && selectable.every((id) => ticked.has(id));
  const disabled = planning || running;
  const toggle = (id: number) => setTicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <section className="space-y-3" aria-labelledby={`legs-title-${channelId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 id={`legs-title-${channelId}`} className="text-sm font-semibold text-dark-100">{t('linear.legs.titleActions')}</h3>
          <p className="text-xs text-dark-400 max-w-prose">{t('linear.legs.subtitleActions')}</p>
        </div>
        <button type="button" onClick={() => load(true)} disabled={loading} className="btn-secondary text-sm" title={t('linear.legs.refreshHint')}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {t('linear.legs.refresh')}
        </button>
      </div>

      {failed && (
        <p role="alert" className="text-sm text-error-600 px-3 py-2 rounded-lg border border-error-100 bg-error-50">
          {servers ? t('linear.legs.refreshFailed') : t('linear.legs.loadFailed')}
        </p>
      )}
      {!servers && loading && (
        <div className="flex items-center gap-2 text-sm text-dark-400"><Loader2 size={14} className="animate-spin" />{t('linear.legs.loading')}</div>
      )}
      {servers && servers.length === 0 && <p className="text-sm text-dark-400">{t('linear.legs.noServers')}</p>}

      {servers && servers.map((srv) => {
        const leftover = notDesiredLegs(srv);
        const reason = srv.error ? t(serverFailureReasonKey(srv.error)) : t('linear.legs.unreachableUnknown');
        return (
          <div key={srv.id} className={`rounded-lg border ${ticked.has(srv.id) ? 'border-primary-500' : 'border-dark-700'}`}>
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-dark-700">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={ticked.has(srv.id)}
                disabled={!srv.reachable || disabled}
                onChange={() => toggle(srv.id)}
                aria-label={t('linear.legs.selectServer', { server: srv.name })}
              />
              <span className="font-medium text-sm text-dark-100">{srv.name}</span>
              {(() => { const b = serverTypeBadge(srv.type); return <span className={`text-[11px] px-1.5 py-0.5 rounded border ${b.className}`}>{b.labelKey ? t(b.labelKey) : b.label}</span>; })()}
              <span className={`text-xs ${srv.reachable ? 'badge-success' : srv.stateAgeMs === null ? 'badge-neutral' : 'badge-warning'}`}>{t(serverStateKey(srv))}</span>
              <span className="text-xs text-dark-400">{ago(srv.stateAgeMs)}</span>
            </div>
            {!srv.reachable && (
              <p className="px-3 pt-2 text-xs text-warning-600">
                {t('linear.legs.unreachableDisabled', { reason })}{' '}
                {srv.stateAgeMs === null ? t('linear.legs.unreachableNoState') : t('linear.legs.unreachableWithState', { ago: ago(srv.stateAgeMs) })}
              </p>
            )}
            {srv.reachable && !srv.complete && <p className="px-3 pt-2 text-xs text-warning-600">{t('linear.legs.incompleteList')}</p>}
            {srv.reachable && leftover.length > 0 && (
              <div className="mx-3 mt-2 flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-lg border border-warning-100 bg-warning-50 text-warning-600 text-sm">
                <span>{t('linear.legs.notDesiredNotice', { count: leftover.length, server: srv.name })}</span>
                <button type="button" disabled={disabled} className="btn-secondary text-xs text-error-600"
                  onClick={() => ask('delete', { servers: [srv.id], legIds: leftover.map((r) => r.leg!.id) })}>
                  <Trash2 size={12} />
                  {t('linear.legs.notDesiredDelete', { count: leftover.length })}
                </button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('linear.resolutionCol')}</th>
                    <th>{t('linear.xaccelChannelNameCol')}</th>
                    <th>{t('linear.dbStatus')}</th>
                    <th>{t('linear.realStatus')}</th>
                    <th>{t('linear.legs.verdictCol')}</th>
                    <th>{t('linear.legs.lastActionCol')}</th>
                    <th>{t('linear.legs.actionCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {srv.rows.length === 0 && <tr><td colSpan={7} className="text-xs text-dark-400">{t('linear.legs.noRows')}</td></tr>}
                  {srv.rows.map((row, i) => {
                    const real = row.real;
                    const uptime = typeof real === 'object' ? formatUptime(real.uptime) : '';
                    return (
                      <tr key={`${row.resolutionId}-${row.leg?.id ?? 'x'}-${i}`} className={row.desired ? '' : 'opacity-75'}>
                        <td>
                          <span className="badge-primary text-xs">{row.resolutionName}</span>
                          {!row.desired && <span className="block text-[11px] text-dark-400 mt-0.5">{t('linear.legs.notDesired')}</span>}
                        </td>
                        <td className="text-xs font-mono text-dark-200 break-all">
                          {row.name ?? row.leg?.name ?? ''}
                          {row.verdict === 'stale-name' && row.expectedName && <span className="block text-[11px] text-dark-400">→ {row.expectedName}</span>}
                          {row.nameAccepted && <span className="block text-[11px] text-dark-400" title={t('linear.legs.nameAcceptedHint')}>{t('linear.legs.nameAccepted')}</span>}
                        </td>
                        <td className="text-xs">
                          {row.leg ? row.leg.dbStatus : <span className="text-dark-400">{t('linear.legs.noDbRow')}</span>}
                          {(() => { const h = heldBadge(row.leg); return h ? <span className="block mt-0.5 text-[11px] badge-neutral w-fit" title={t(h.hintKey)}>{t(h.labelKey)}</span> : null; })()}
                        </td>
                        <td className="text-xs">
                          {t(realStatusKey(real))}
                          {uptime && <span className="text-dark-400"> · {uptime}</span>}
                          {typeof real === 'object' && typeof real.clients === 'number' && real.clients > 0 && <span className="text-dark-400"> · {t('linear.legs.clients', { count: real.clients })}</span>}
                          {!srv.reachable && typeof real === 'object' && <span className="block text-[11px] text-dark-400">{t('linear.legs.lastSeenState')}</span>}
                        </td>
                        <td><span className={`text-xs whitespace-nowrap ${verdictClass(row.verdict)}`} title={t(verdictHintKey(row.verdict))}>{t(verdictLabelKey(row.verdict))}</span></td>
                        <td className="text-xs max-w-[220px] break-words">
                          {row.leg?.lastAction && <span className="block text-dark-400">{t(`linear.legs.action.${row.leg.lastAction}`, { defaultValue: row.leg.lastAction })}{row.leg.lastActionAt ? ` · ${formatLastActionAt(row.leg.lastActionAt)}` : ''}</span>}
                          {row.leg?.lastError && <span className="block text-error-600">{row.leg.lastError}</span>}
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {(row.actions ?? []).map((a) => (
                              <button key={a} type="button" disabled={disabled} title={t(`linear.legs.actionHint.${a}`)}
                                onClick={() => ask(a, rowScope(srv.id, row))}
                                className={`px-1.5 py-0.5 rounded border text-[11px] ${DANGER_ACTIONS.includes(a) ? 'border-error-100 text-error-600 hover:bg-error-50' : 'border-dark-600 text-dark-200 hover:bg-dark-700'}`}>
                                {t(`linear.legs.action.${a}`)}
                              </button>
                            ))}
                            {disabledRowActions(srv.type, row).map((d) => (
                              <button key={`off-${d.action}`} type="button" disabled aria-disabled="true" title={t(d.reasonKey)}
                                className="px-1.5 py-0.5 rounded border text-[11px] border-dark-700 text-dark-500 cursor-not-allowed">
                                {t(`linear.legs.action.${d.action}`)}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {servers && servers.length > 0 && (
        <div className="rounded-lg border border-dark-700 px-3 py-3 space-y-2">
          <label className="flex items-center gap-2 text-sm text-dark-200">
            <input type="checkbox" className="h-4 w-4" checked={allTicked} disabled={selectable.length === 0 || disabled}
              onChange={() => setTicked(toggleAllServers(servers, ticked))} />
            {t('linear.legs.selectAllServers')}
            <span className="text-xs text-dark-400">({t('linear.legs.selectAllHint')})</span>
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm text-dark-300" htmlFor={`leg-bar-${channelId}`}>{t('linear.legs.barChoose')}</label>
            <select id={`leg-bar-${channelId}`} className="input w-auto text-sm" value={barAction} disabled={disabled}
              onChange={(e) => setBarAction(e.target.value as LegActionName)}>
              {BAR_ACTIONS.map((a) => <option key={a} value={a}>{t(`linear.legs.action.${a}`)}</option>)}
            </select>
            <button type="button" disabled={disabled || bar.count === 0}
              className={DANGER_ACTIONS.includes(barAction) ? 'btn-primary bg-error-600 hover:bg-error-500 disabled:opacity-50 text-sm' : 'btn-primary disabled:opacity-50 text-sm'}
              onClick={() => ask(barAction, { servers: [...ticked].filter((id) => selectable.includes(id)) })}>
              {(planning || running) && <Loader2 size={14} className="animate-spin" />}
              {bar.count > 0
                ? t(`linear.legs.bar.${barAction}`, { count: bar.count, servers: bar.serverNames.join(', ') })
                : t(`linear.legs.action.${barAction}`)}
            </button>
          </div>
          <p className="text-xs text-dark-400">
            {ticked.size === 0 ? t('linear.legs.barNoServer') : bar.count === 0 ? t('linear.legs.barNothing') : t(`linear.legs.actionHint.${barAction}`)}
          </p>
        </div>
      )}

      {results && (
        <div role="status" className="rounded-lg border border-dark-700 px-3 py-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-dark-100">
              {t('linear.legs.results.title', { action: t(`linear.legs.action.${results.action}`) })}
              <span className="ml-2 text-xs text-dark-400">
                {(() => { const s = resultSummary(results.results); return t('linear.legs.results.summary', { done: s.done, already: s.alreadyDone, failed: s.failed }); })()}
              </span>
            </p>
            <button type="button" className="p-1 rounded hover:bg-dark-700 text-dark-400" onClick={() => setResults(null)} aria-label={t('linear.legs.results.close')}><X size={14} /></button>
          </div>
          <ul className="space-y-0.5">
            {results.results.map((r, i) => (
              <li key={`${r.serverId}-${r.resolutionId}-${i}`} className="text-xs">
                <span className={r.ok ? (r.alreadyDone ? 'text-dark-400' : 'text-success-600') : 'text-error-600'}>
                  {r.ok ? (r.alreadyDone ? t('linear.legs.results.alreadyDone') : t('linear.legs.results.ok')) : t('linear.legs.results.failed')}
                </span>
                {' '}{r.serverName}: <span className="font-mono">{r.legName}</span>
                {r.skipped && <span className="text-dark-400"> ({t(skipReasonKey(r.skipped))})</span>}
                {r.error && !r.ok && <span className="text-error-600"> {r.error}</span>}
              </li>
            ))}
          </ul>
          {(() => {
            const retry = retryScope(results.results);
            return retry ? (
              <button type="button" className="btn-secondary text-xs" disabled={disabled} onClick={() => ask(results.action, retry)}>
                <RefreshCw size={12} />{t('linear.legs.results.retry')}
              </button>
            ) : null;
          })()}
        </div>
      )}

      {servers && servers.length > 0 && (
        <details className="text-xs text-dark-400">
          <summary className="cursor-pointer select-none">{t('linear.legs.legend')}</summary>
          <dl className="mt-2 grid gap-1.5 sm:grid-cols-2">
            {LEG_VERDICTS.map((v) => (
              <div key={v} className="flex items-start gap-2">
                <dt className="shrink-0"><span className={`text-xs whitespace-nowrap ${verdictClass(v)}`}>{t(verdictLabelKey(v))}</span></dt>
                <dd>{t(verdictHintKey(v))}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
    </section>
  );
}
